// src/components/auth/AuthModal.tsx
import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody } from '@/components/ui/Modal';
import { useResponsive } from '@/hooks/useResponsive';
import { useLanguage } from '@/hooks/useLanguage';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import PasswordResetRequestForm from './PasswordResetRequestForm';

// Стили для скрытия скроллбара и безопасной области
const noScrollbarStyles = `
.no-scrollbar {
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
  scrollbar-width: none;  /* Firefox */
}
.no-scrollbar::-webkit-scrollbar { 
  display: none;  /* Safari and Chrome */
}
`;

export type AuthModalView = 'login' | 'register' | 'passwordReset';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialView?: AuthModalView;
  selectedPlan?: string | null;
}

const AuthModal: React.FC<AuthModalProps> = ({ 
  isOpen, 
  onClose, 
  initialView = 'login',
  selectedPlan = null
}) => {
  const [view, setView] = useState<AuthModalView>(initialView);
  const { t } = useLanguage();
  const { isMobile } = useResponsive();
  
  // Reset view when modal opens/closes or initialView changes
  useEffect(() => {
    if (isOpen) {
      setView(initialView);
    }
  }, [isOpen, initialView]);

  // Функция для получения названия тарифа
  const getPlanName = (planId: string | null): string => {
    if (!planId) return '';
    
    switch(planId) {
      case 'basic':
        return 'Базовый';
      case 'business':
        return 'Бизнес';
      case 'premium':
        return 'Премиум';
      default:
        return planId;
    }
  };

  const getModalTitle = () => {
    switch (view) {
      case 'login':
        return t('auth.login.title');
      case 'register':
        return t('auth.register.title');
      case 'passwordReset':
        return t('auth.passwordReset.title');
      default:
        return t('auth.login.title');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={isMobile ? "full" : "md"}
      mobileMode={isMobile ? "fullscreen" : "normal"}
      draggable={!isMobile}
      swipeToDismiss={isMobile}
      className={isMobile ? "safe-area-padding" : ""}
    >
      {/* Внедряем стили для скрытия скроллбара и безопасной области */}
      <style>{`
        ${noScrollbarStyles}
        
        /* Дополнительные стили для учета безопасной области мобильных браузеров */
        .safe-area-padding {
          padding-top: env(safe-area-inset-top, 20px);
        }
        
        /* Увеличение размера области захвата на мобильных устройствах */
        @media (max-width: 640px) {
          .modal-close-button {
            width: 44px !important;
            height: 44px !important;
            top: env(safe-area-inset-top, 10px);
            right: 10px;
          }
        }
      `}</style>

      <ModalHeader 
        onClose={onClose} 
        className={isMobile ? "pt-8 sm:pt-4" : ""}
      >
        {getModalTitle()}
      </ModalHeader>
      
      <ModalBody scrollable>
        {/* Отображение выбранного тарифа, если он есть */}
        {selectedPlan && view === 'register' && (
          <div className="mb-4 p-3 bg-[#EFF6EF] rounded-md text-center">
            <p className="text-[#245D33] font-medium">
              Вы выбрали тариф: <strong>{getPlanName(selectedPlan)}</strong>
            </p>
          </div>
        )}
        
        {view === 'login' ? (
          <LoginForm 
            onSwitchToRegister={() => setView('register')} 
            onSwitchToPasswordReset={() => setView('passwordReset')} 
          />
        ) : view === 'register' ? (
          <RegisterForm 
            onSwitchToLogin={() => setView('login')} 
            selectedPlan={selectedPlan}
          />
        ) : (
          <PasswordResetRequestForm 
            onSwitchToLogin={() => setView('login')} 
          />
        )}
      </ModalBody>
    </Modal>
  );
};

export default AuthModal;